import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Checkbox, Empty } from 'antd'

import { SortableItem } from './SortableItem'

import type { IResolvedFilterField } from '../../Helpers/tableSettings'
import type { Dispatch, SetStateAction } from 'react'

interface FilterFieldsTabProps {
    fields: IResolvedFilterField[]
    onChange: Dispatch<SetStateAction<IResolvedFilterField[]>>
}

/**
 * Вкладка «Фільтр»: DnD-сортування + чекбокс видимості кожного поля.
 */
export const FilterFieldsTab = ({ fields, onChange }: FilterFieldsTabProps) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const oldIndex = fields.findIndex((field) => field.key === active.id)
        const newIndex = fields.findIndex((field) => field.key === over.id)
        if (oldIndex < 0 || newIndex < 0) return

        onChange(arrayMove(fields, oldIndex, newIndex))
    }

    const updateField = (key: string, patch: Partial<IResolvedFilterField>) => {
        onChange(fields.map((field) => (field.key === key ? { ...field, ...patch } : field)))
    }

    if (fields.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fields.map((field) => field.key)} strategy={verticalListSortingStrategy}>
                {fields.map((field) => (
                    <SortableItem key={field.key} id={field.key}>
                        <Checkbox
                            checked={field.visible}
                            onChange={(e) => updateField(field.key, { visible: e.target.checked })}
                        />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {field.label}
                        </span>
                    </SortableItem>
                ))}
            </SortableContext>
        </DndContext>
    )
}
